import * as React from 'react';
import { Box, Button, ClickableBox, Flex } from 'components/ui';
import { AlbumArt } from 'components/Playlist/BeatRow';
import { useMediaById, usePrintIdToOriginal } from 'hooks';
import { LayoutProps, SpaceProps } from 'styled-system';
import { useState } from 'react';
import { ArrowDown, ArrowUp } from 'components/ui/Arrows';
import capitalize from 'lodash/capitalize';
import { StakeBalance } from 'components/common';
import useStakingContracts from 'hooks/useStakingContracts';
import { useQueryClient } from 'react-query';

import { useEffect } from 'react';
import { LOGO_GRADIENT } from 'utils/colors';
import Tooltip from 'components/ui/Tooltip';

interface StakeRowProps {
  pairId: number;
  genesisId: string;
  enigmaId: string;
  balance: StakeBalance;
  isApproved: boolean;
  index: number;
  readOnly?: boolean;
  capReached?: boolean;
}

interface StakeSectionProps {
  text?: string;
  type: 'stake' | 'unstake';
  balance: number;
  onClick(arg1: number): any;
  disabled: boolean;
  defaultCount?: number;
  buttonColor?: string;
}

const StakeSection: React.FC<StakeSectionProps & SpaceProps & any> = ({
  type,
  balance,
  onClick,
  disabled = false,
  defaultCount = 0,
  buttonColor = 'primary',
  ...rest
}) => {
  const [count, setCount] = useState<number>(0);

  function incrementCount() {
    if (count === balance) return;
    setCount(last => last + 1);
  }

  function decrementCount() {
    if (count === 0) return;
    setCount(last => last - 1);
  }

  useEffect(() => {
    if (count > balance) {
      return setCount(balance);
    }

    if (count === 0 && balance > 0) {
      return setCount(defaultCount);
    }
    // note: do not want this to trigger when count updates
    // eslint-disable-next-line
  }, [balance, defaultCount]);

  return (
    <Flex {...rest} wrap>
      <Flex alignCenter width="100%" pr="3">
        <Box mr="3" fontSize="1">
          {type === 'stake' ? 'Available:' : 'Staked:'}
        </Box>
        <ClickableBox
          fontSize="2"
          borderBottomColor="highlight"
          borderBottom="3px solid"
          onClick={() => setCount(balance)}
        >
          {balance} pairs
        </ClickableBox>
      </Flex>

      <Flex width="100%" mt="3">
        <Button
          width="124px"
          boxShadow="1"
          fontWeight="600"
          borderRadius="10px"
          p="1em"
          fontSize="1"
          onClick={() => {
            onClick(count);
          }}
          disabled={disabled}
          background={disabled ? 'primary' : buttonColor}
          data-tip="stakingTip"
          data-for={rest.dataFor}
        >
          {capitalize(type)} {count} pairs
        </Button>

        <Flex column alignCenter justifyCenter height="100%" ml="1">
          <ArrowUp color="primary" size={10} mb="1" onClick={incrementCount} disabled={disabled} />
          <ArrowDown color="primary" size={10} onClick={decrementCount} disabled={disabled} />
        </Flex>
      </Flex>
    </Flex>
  );
};

const StakeRow: React.FC<StakeRowProps & SpaceProps & LayoutProps> = ({
  pairId,
  genesisId,
  enigmaId,
  balance,
  isApproved,
  index,
  readOnly = false,
  capReached = false,
  ...rest
}) => {
  const genesisMedia = useMediaById(genesisId);
  const enigmaMedia = useMediaById(enigmaId);
  const printsToOriginals = usePrintIdToOriginal();
  // note - this assumes same track number for both prints in pair
  const genesisPrint = printsToOriginals[genesisId] ?? {};
  const enigmaPrint = printsToOriginals[enigmaId] ?? {};
  const trackNumber = genesisPrint.mintNumber;

  const bgTransparency = (index % 2) * 0.4 + 0.1;

  const { stake, unstake } = useStakingContracts();
  const queryClient = useQueryClient();

  const stakePair = async (count: number) => {
    try {
      await stake(pairId, count, () => {
        console.log(`Staked ${count} of pair ${pairId}`);
        queryClient.invalidateQueries('balances');
        queryClient.invalidateQueries('stakedBalances');
        queryClient.invalidateQueries('totalStaked');
      });
    } catch (e) {
      console.error(e);
    }
  };

  const unstakePair = async (count: number) => {
    try {
      await unstake(pairId, count, () => {
        console.log(`Unstaked ${count} of pair ${pairId}`);
        queryClient.invalidateQueries('balances');
        queryClient.invalidateQueries('stakedBalances');
        queryClient.invalidateQueries('totalStaked');
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Flex
      width="100%"
      justifyContent={readOnly ? 'space-around' : ['center', 'space-between']}
      wrap
      background={`rgba(0, 0, 0, ${bgTransparency})`}
      {...rest}
    >
      <Flex column alignCenter justifyCenter flexGrow={0} pb={['2', null]} ml={[0, 0, '4']}>
        <Box mb="2">Genesis LP + Enigma LP #{trackNumber}</Box>
        <Flex>
          <AlbumArt imgUrl={genesisMedia.image} />
          <AlbumArt ml={'-1.5em'} imgUrl={enigmaMedia.image} />
        </Flex>
      </Flex>

      <Box display={readOnly ? 'block' : ['none', 'none', 'block']} mt="2" flexGrow={0}>
        <Box textAlign="end">
          <Box fontSize="1" mt="1">
            Genesis supply: {genesisPrint.print?.supply}
          </Box>
          <Box fontSize="1" mt="1">
            Enigma supply: {enigmaPrint.print?.supply}
          </Box>
          <Box fontSize="1" mt="1">
            Total staked: {balance.totalStaked}
          </Box>
        </Box>
      </Box>

      {!readOnly && (
        <Flex flexGrow={0} mr={[0, '4']}>
          <StakeSection
            type="stake"
            mr="2"
            flex="0 auto"
            width="175px"
            balance={balance.pair}
            onClick={stakePair}
            disabled={!isApproved || balance.pair === 0 || capReached}
            defaultCount={1}
            buttonColor={LOGO_GRADIENT}
            dataFor="stakingRewardTip"
          />

          <StakeSection
            type="unstake"
            flex="0 auto"
            width="175px"
            balance={balance.staked}
            onClick={unstakePair}
            disabled={balance.staked === 0}
            dataFor="unstakingRewardTip"
            defaultCount={1}
          />

          <Tooltip id="stakingRewardTip" place="bottom" effect="solid">
            Staking will automatically claim all available reward
          </Tooltip>
          <Tooltip id="unstakingRewardTip" place="bottom" effect="solid">
            Unstaking will automatically claim all available reward
          </Tooltip>
        </Flex>
      )}
    </Flex>
  );
};

export default StakeRow;
